/**
 * **Treehouse Hotel Own System**
 *
 * @exports bs_360
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = {};
    /**
       * @memberOf module:bs_360
       * @see {@link boot}
       *
       * @param {object} ctx - The context object.
       */
    engine.boot = function boot(ctx) {
        const stepBarContainer = document.querySelector('.bk-flow-components');
        if (stepBarContainer) {
            const refreshPage = thn.debounce(thn.pageCheck, 150);
            thn.observeDom(stepBarContainer, refreshPage, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.shutdown(() => {
                refreshPage?.cancel();
                thn.stopObserveDom(stepBarContainer, refreshPage);
            });
        }
    };
    /**
       * @memberOf module:bs_360
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const container = document.querySelector('#bk-flow-components-content-container');
        if (container) {
            let refreshPrice = thn.debounce(() => ctx.widget.refresh(), 800);
            thn.observeDom(container, refreshPrice, {
                childList: true,
                subtree: true,
            });
            thn.pageUnload(() => {
                refreshPrice.cancel();
                thn.stopObserveDom(container, refreshPrice);
                refreshPrice = null;
            });
        }
        const multipleRoomSelector = document.querySelector('.bk-multi-room-steps');
        if (multipleRoomSelector) {
            let multipleRoomObserver = thn.debounce(() => ctx.widget.refresh(), 150);
            thn.observeDom(multipleRoomSelector, multipleRoomObserver, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.pageUnload(() => {
                multipleRoomObserver.cancel();
                thn.stopObserveDom(multipleRoomSelector, multipleRoomObserver);
                multipleRoomObserver = null;
            });
        }
    };
    /**
       * @memberOf module:bs_360
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        // Dates
        const dateBeginUrl = thn.parseUrlParameter('startDate');
        const dateEndUrl = thn.parseUrlParameter('endDate');
        if (dateBeginUrl && dateEndUrl) {
            widgetData.dateBegin = dateBeginUrl.replace(/-/g, '/');
            widgetData.dateEnd = dateEndUrl.replace(/-/g, '/');
        }
        const roomsInfo = [...document.querySelectorAll('[id*=bk-flow-room][class*=card-entry]')].filter((el) => [...el.querySelectorAll('.price')].length).map((el) => {
            // Price
            const prices = [...el.querySelectorAll('.bk-flow-room__price-content .price')].map((price) => thn.parseNumber(price?.textContent));
            if (prices.length) {
                return {
                    price: Math.min(...prices),
                    childrenAges: [],
                };
            }
            return false;
        }).filter(Boolean);
        roomsInfo.sort((a, b) => a.price - b.price);
        // Currency
        const currency = thn.parseCurrency((document.querySelector('.price.u-font-bold'))?.textContent) || thn.parseUrlParameter('currency');
        // Guests: Adults & Children
        const adults = thn.parseNumber(document.querySelector('.bk-flow-navigation-details__entries.include-sidebar > div > div:nth-child(3) span:nth-child(1)')?.textContent[0]) || 2;
        const children = thn.parseNumber(document.querySelector('.bk-flow-navigation-details__entries.include-sidebar > div > div:nth-child(3) span:nth-child(2)')?.textContent[2]) || 0;
        const guests = adults + children;
        widgetData.adults = adults;
        widgetData.children = children;
        widgetData.guests = guests;
        // Special Prices
        if (thn.parseUrlParameter('ratePlanCode')) {
            widgetData.priceSpecial = 1;
        }
        if (roomsInfo.length && currency) {
            widgetData.price = roomsInfo[0].price;
            widgetData.currency = currency;
        }
        if (!roomsInfo.length && thn.parseUrlParameter('roomTypeCode')) {
            // We only flag as special when 'roomTypeCode' filter is present in the url params if there is no rooms
            // as a no availability in a filtered search does not mean that there are no rooms, just that there are no rooms that match the filter.
            // Marking it as special makes it to not appear on the "No vacancies details" report.
            widgetData.priceSpecial = 1;
        }
        const noAvailability = !!document.querySelector('#bk-flow-room-content > h5');
        if (noAvailability) {
            widgetData.noRooms = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_360
       * @this thn.widgets.booking
       * @see {@link Booking#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingObserveDom = function bookingObserveDom(ctx) {
        const dataLayer = window.dataLayer;
        if (!dataLayer || engine.treehouseBookingObserver) {
            return;
        }
        engine.treehouseBookingObserver = new thn.DataLayerHelper(window.dataLayer, function (model, message) {
            if (message.event === 'triptease-reservation' && ctx.widget.setData().isSet()) {
                ctx.widget.save();
            }
        }, true);
        thn.pageUnload(() => {
            engine.treehouseBookingObserver.cancel();
            delete engine.treehouseBookingObserver;
        });
    };
    /**
       * @memberOf module:bs_360
       * @this thn.widgets.booking
       * @see {@link Booking#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        const bookingId = thn.objectGet(window.dataLayer, 'reservationId');
        if (!bookingId) {
            widgetData.set = false;
            return widgetData;
        }
        // Dates
        widgetData.dateBegin = thn.objectGet(window.dataLayer, 'checkinTime')?.replace(/-/g, '/');
        widgetData.dateEnd = thn.objectGet(window.dataLayer, 'checkoutTime')?.replace(/-/g, '/');
        // Prices & currency
        const price = thn.parseNumber(thn.objectGet(window.dataLayer, 'totalPrice'));
        if (!thn.empty(price)) {
            widgetData.price = price;
            widgetData.currency = thn.objectGet(window.dataLayer, 'priceCurrency');
        }
        // Guests, Adults & Children
        widgetData.adults = (thn.parseNumber(thn.parseUrlParameter('adults')) || 2);
        widgetData.children = (thn.parseNumber(thn.parseUrlParameter('children')) || 0);
        widgetData.guests = widgetData.adults + widgetData.children;
        // bookingId
        widgetData.bookingId = bookingId;
        return widgetData;
    };
    return engine;
}

export { factory };
